// Rug Room Shots: shared constants, formatters and small components.
// Contract: rug-room-shots-worker/SPEC.md (sections 3.4 and 5) and the 2026-10-21 build spec (section 9.2).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Chip, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, ToggleButtonGroup, ToggleButton, CircularProgress, Stack, Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdClose, MdCallSplit, MdAutoAwesome, MdHideImage, MdWbSunny, MdWarningAmber,
  MdCompareArrows, MdDownload, MdBrokenImage, MdDeck, MdLightMode, MdBedroomParent,
} from 'react-icons/md';
import { TOOL_COLORS } from '../../constants/colors';
import { ROOM_LETTERS } from './roomRules';

export const AI_COLOR = TOOL_COLORS.AI.primary;
export const AI_HOVER = TOOL_COLORS.AI.hover;
export const NY_TZ = 'America/New_York';

// ---------------------------------------------------------------------------
// Vocabularies (the backend /config is the source of truth; these are fallbacks
// so the page still renders while /config loads or if it fails).
// ---------------------------------------------------------------------------
export const DEFAULT_STYLES = [
  { code: 'BH', name: 'Boho' },
  { code: 'CR', name: 'California Rustic' },
  { code: 'CO', name: 'Coastal' },
  { code: 'MC', name: 'Modern Classic' },
  { code: 'UM', name: 'Urban Modern' },
  { code: 'OU', name: 'Outdoor' },
  // Sunroom comes only from the Outdoor & sunroom list (Rooms tab), never from a person (build spec 3.10).
  { code: 'SU', name: 'Sunroom', manual: false },
];

// Every style PICKER (plan style change, Reject -> "Correct style", the compare view's style chips) offers only
// these. Style FILTERS, chips, colours and the Add room style list keep the full list.
export const manualStyles = (styles) => (styles || []).filter((s) => s && s.manual !== false && s.code !== 'SU');

export const DEFAULT_SHAPES = [
  { code: 'AC', name: 'Accent' },
  { code: 'SR', name: 'Small/Medium Rectangle' },
  { code: 'LR', name: 'Large Rectangle' },
  { code: 'OS', name: 'Oversized' },
  { code: 'RO', name: 'Round' },
  { code: 'SQ', name: 'Square' },
  { code: 'RU', name: 'Runner' },
];

// The AI check's estimate per image before /config answers (the worker's dated price list, config check.price_changes:
// gemini-4-argon doubles at midnight New York on 2026-12-01). /config's aiCheck.usdPerCheck is the live value.
export const AI_PRICE_CHANGE_MS = Date.UTC(2026, 11, 1, 5, 0, 0);
export const aiUsdPerCheckFallback = (nowMs) => (nowMs >= AI_PRICE_CHANGE_MS ? 0.045 : 0.025);

export const DEFAULT_CONFIG = {
  model: 'gemini-3.1-flash-image',
  imageSize: '4K',
  aspectRatio: '1:1',
  speed: 'fast',
  usdPerImage: 0.151,
  usdPerImageBatch: 0.076,
  // GET /config "speeds" (SPEC 9.10). Until the backend says otherwise, Half price is off.
  speeds: null,
  maxIdsPerJob: 5000,
  styles: DEFAULT_STYLES,
  shapes: DEFAULT_SHAPES,
  // Order matters: the compare view picks a reason with the number keys 1-8 by position.
  rejectReasons: ['rug_wrong', 'shape_wrong', 'scale_wrong', 'room_changed', 'artifacts', 'wrong_style', 'other', 'rug_binding'],
  // Every feature is off until /config says the worker supports it (SPEC 10, build spec 5.1).
  features: {
    addRoom: false,
    addRoomReason: 'loading',
    addStyle: false,
    addStyleReason: 'loading',
    detailPhotos: false,
    detailPhotosReason: 'loading',
    zipExports: false,
    zipExportsReason: 'loading',
    sceneList: false,
    roomRules: false,
    // AI check (SPEC_AICHECK 4.2): on when the worker's heartbeat says rrs-0.5.0 runs the checks.
    aiCheck: false,
    aiCheckReason: 'loading',
  },
  // GET /config "aiCheck": the effective switch and Pass / Fail scores, the price estimate per check, and whether
  // this person may change them (admins / owners).
  aiCheck: {
    autoCheck: true, passMin: 85, failBelow: 70, usdPerCheck: aiUsdPerCheckFallback(Date.now()), canEdit: false,
  },
  roomLetters: ROOM_LETTERS,
  maxRoomUploadBytes: 41943040,
  manualRoomNumberFrom: 51,
  exportMaxPartBytes: 2000000000,
  exportLinkMinutes: 15,
  downloadLinkMinutes: 30,
};

export const STYLE_COLORS = {
  BH: '#B5651D',
  CR: '#8D6E63',
  CO: '#1E88E5',
  MC: '#6D4C41',
  UM: '#455A64',
  OU: '#2E7D32',
  SU: '#F9A825',
};

// Styles added on the Rooms tab have no fixed colour: a stable pick from this palette by code.
const HASH_PALETTE = ['#C2185B', '#7B1FA2', '#00838F', '#EF6C00', '#5D4037', '#558B2F', '#3949AB', '#AD1457'];
const hashCode = (str) => {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) h = ((h * 31) + s.charCodeAt(i)) >>> 0;
  return h;
};
export const styleColor = (code) => STYLE_COLORS[code] || (code ? HASH_PALETTE[hashCode(code) % HASH_PALETTE.length] : '#757575');

export const REJECT_REASON_LABELS = {
  rug_wrong: 'Rug wrong',
  shape_wrong: 'Shape wrong',
  scale_wrong: 'Scale wrong',
  room_changed: 'Room changed',
  artifacts: 'Artifacts',
  wrong_style: 'Wrong style',
  other: 'Other',
  rug_binding: 'Rug binding',
};

export const PLAN_STATUS = {
  planned: { label: 'Ready', color: 'success' },
  held: { label: 'Held', color: 'warning' },
  gap: { label: 'Gap (no room)', color: 'error' },
  skipped_existing: { label: 'Has room photo', color: 'default' },
  excluded: { label: 'Excluded', color: 'default' },
  replanning: { label: 'Re-planning', color: 'info' },
};

export const GEN_STATUS = {
  queued: { label: 'Queued', color: 'default' },
  running: { label: 'Generating', color: 'info' },
  batched: { label: 'Preparing batch', color: 'info' },
  submitted: { label: 'At Google (half price)', color: 'secondary' },
  succeeded: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  unknown: { label: 'Unknown outcome', color: 'warning' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

// A generation is in progress in any of these (SPEC 9.3).
export const IN_PROGRESS_STATUSES = ['queued', 'running', 'batched', 'submitted'];
export const AT_GOOGLE_STATUSES = ['batched', 'submitted'];

export const REVIEW_STATE = {
  pending: { label: 'Needs review', color: 'default' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
};

export const JOB_STATUS = {
  planning: { label: 'Planning', color: 'info' },
  planned: { label: 'Planned', color: 'primary' },
  plan_failed: { label: 'Plan failed', color: 'error' },
  running: { label: 'Running', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

export const STYLE_FLAG_INFO = {
  two_style: { icon: MdCallSplit, text: 'This colorway sells in two styles; the newest label won.' },
  design_split: { icon: MdCompareArrows, text: 'Other colorways of this design are placed in different styles.' },
  model_pick: { icon: MdAutoAwesome, text: 'Picked by the Style Sorter model (no human label yet).' },
  no_embedding: { icon: MdHideImage, text: 'No image vector was available, so only attributes were used.' },
  outdoor: { icon: MdWbSunny, text: 'Outdoor rug (rug_type rule).' },
  room_style_mismatch: { icon: MdWarningAmber, text: 'The chosen room belongs to a different style.' },
  // Outdoor & sunroom list and room-type rules (build spec 9.2).
  scene_outdoor: { icon: MdDeck, text: 'On the Outdoor & sunroom list: outdoor room.' },
  scene_sun: { icon: MdLightMode, text: 'On the Outdoor & sunroom list: sunroom.' },
  scene_both: { icon: MdCallSplit, text: 'On the Outdoor & sunroom list: outdoor room plus a sunroom shot.' },
  sun_twin: { icon: MdLightMode, text: 'Sunroom shot of an outdoor + sunroom rug.' },
  room_rule_mismatch: { icon: MdBedroomParent, text: 'The chosen room is not the usual type for this rug.' },
  room_base_shape_mismatch: { icon: MdWarningAmber, text: "The room's base photo shows an oval rug." },
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const parseTs = (ts) => {
  if (!ts) return null;
  const s = String(ts);
  const d = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const nyFmtShort = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const nyFmtFull = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ, year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
});
const nyFmtTime = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ, hour: 'numeric', minute: '2-digit', second: '2-digit',
});

const nyFmtDate = new Intl.DateTimeFormat('en-US', { timeZone: NY_TZ, year: 'numeric', month: 'short', day: 'numeric' });

export const fmtNy = (ts) => { const d = parseTs(ts); return d ? nyFmtShort.format(d) : ''; };
export const fmtNyDate = (ts) => { const d = parseTs(ts); return d ? nyFmtDate.format(d) : ''; };
export const fmtNyFull = (ts) => { const d = parseTs(ts); return d ? nyFmtFull.format(d) : ''; };
export const fmtNyTime = (ts) => { const d = parseTs(ts); return d ? nyFmtTime.format(d) : ''; };
export const tsMs = (ts) => { const d = parseTs(ts); return d ? d.getTime() : null; };

// "Oct 07 2026, 02-15-09 PM" (checklist item 10: Eastern time in file names).
export const easternFileStamp = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY_TZ, year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  }).formatToParts(new Date());
  const p = (t) => (parts.find((x) => x.type === t) || {}).value || '';
  return `${p('month')} ${p('day')} ${p('year')}, ${p('hour')}-${p('minute')}-${p('second')} ${p('dayPeriod').toUpperCase()}`;
};

export const fmtUsd = (n, digits = 2) => {
  const v = Number(n);
  if (n === null || n === undefined || Number.isNaN(v)) return '-';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

// The worker's next dated AI check price (/config or settings "usdPerCheckNext": {usd, from}) in words, else ''.
export const aiNextPriceText = (next) => {
  if (!next || !(Number(next.usd) > 0)) return '';
  const day = fmtNyDate(next.from);
  return day ? `about ${fmtUsd(next.usd, 3)} from ${day}` : '';
};

// Price per image: show 3 decimals ($0.151) but never trailing noise.
export const fmtRate = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '-';
  return `$${v.toFixed(v < 1 ? 3 : 2)}`;
};

export const fmtInt = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('en-US'));

// File sizes: "6.63 GB", "204 MB", "9.6 MB", "350 KB" (decimal units, as the download dialogs and S3 report them).
export const fmtSize = (bytes) => {
  const v = Number(bytes);
  if (bytes === null || bytes === undefined || Number.isNaN(v)) return '-';
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GB`;
  if (v >= 1e8) return `${Math.round(v / 1e6)} MB`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} MB`;
  return `${Math.max(0, Math.round(v / 1e3))} KB`;
};

export const fmtElapsed = (ms) => {
  if (ms === null || ms === undefined || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m >= 60) return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
};

// Coarse duration for half-price waits: "40 s", "12 m", "2 h 14 m", "1 d 3 h".
export const fmtDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '';
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} m`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
};

// "3 h 10 m ago" from an ISO timestamp (UTC), relative to `now` (ms).
export const fmtAgo = (ts, now) => {
  const t = tsMs(ts);
  if (!t) return '';
  return `${fmtDuration((now || Date.now()) - t)} ago`;
};

export const styleName = (code, styles) => {
  const s = (styles || DEFAULT_STYLES).find((x) => x.code === code);
  return s ? s.name : code || '';
};
export const styleCodeOf = (name, styles) => {
  const s = (styles || DEFAULT_STYLES).find((x) => x.name === name || x.code === name);
  return s ? s.code : null;
};
export const shapeName = (code, shapes) => {
  const s = (shapes || DEFAULT_SHAPES).find((x) => x.code === code);
  return s ? s.name : code || '';
};

// Sorter decision in words (SPEC 5.3: "already placed", "model 87%", ...).
export const methodWords = (method, confidence) => {
  const pct = confidence === null || confidence === undefined ? '' : ` ${Math.round(Number(confidence) * 100)}%`;
  switch (method) {
    case 'already_placed': return 'already placed';
    case 'same_design': return 'same design';
    case 'model': return `model${pct}`;
    case 'model_attributes_only': return `attributes only${pct}`;
    case 'outdoor_rule': return 'outdoor rule';
    case 'override': return 'override';
    default: return method ? method.replace(/_/g, ' ') : '';
  }
};

// Generation errors in plain words (SPEC 5.3 Review).
export const errorWords = (code, message) => {
  switch (code) {
    case 'safety_block': return 'Google blocked this image (safety).';
    case 'timeout_after_send': return 'Timed out after sending: may have been billed.';
    case 'rate_limited': return 'Google rate-limited the request three times.';
    case 'server_error': return 'Google returned a server error three times.';
    case 'bad_request': return 'Google refused the request (bad request).';
    case 'no_image': return 'Google answered without an image.';
    case 'input_fetch_failed': return 'The silo or base image could not be downloaded.';
    case 'input_unreadable': return 'The silo or base image could not be read (damaged or too large).';
    case 'model_not_found': return 'The model is not available to this key.';
    case 'size_mismatch': return 'The output is not 4K square.';
    case 'batch_create_rejected': return 'Google refused the half-price batch. Nothing was billed.';
    case 'batch_create_unknown': return 'The half-price batch may have been created, but Google never confirmed it: may have been billed.';
    case 'batch_line_error': return `Google returned an error for this image in the half-price batch${message ? `: ${message}` : '.'}`;
    case 'batch_line_missing': return 'Google finished the half-price batch without this image: may have been billed.';
    case 'batch_cancelled': return 'The half-price batch was cancelled before this image ran. Nothing was billed.';
    case 'batch_failed': return `The half-price batch failed before this image ran${message ? `: ${message}` : '.'} Nothing was billed.`;
    case 'batch_expired': return 'Google gave up on this batch after 48 h. Nothing was billed for this image.';
    case 'batch_cancelled_unknown': return 'The half-price batch was cancelled; this image may have run: may have been billed.';
    case 'batch_expired_unknown': return 'Google gave up on this batch after 48 h; this image may have run: may have been billed.';
    case 'batch_failed_unknown': return 'The half-price batch failed; this image may have run: may have been billed.';
    case 'batch_line_undecodable': return 'Google returned a result for this image that could not be read: may have been billed.';
    case 'batch_line_unreadable': return 'Google returned a result line for this image that could not be read: may have been billed.';
    case 'input_too_large': return 'The silo and room images are too large for a half-price batch line. Nothing was billed; use Fast.';
    case 'job_cancelled': return 'Cancelled with the job before the half-price batch was sent. Nothing was billed.';
    case 'internal': return `Worker error${message ? `: ${message}` : '.'}`;
    default:
      if (code) return `${code.replace(/_/g, ' ')}${message ? `: ${message}` : ''}`;
      return message || '';
  }
};

export const SILO_KIND = {
  own: { label: 'own', title: 'Photo of this exact size', color: 'success' },
  sibling: { label: 'borrowed', title: 'Borrowed from another size of the same shape', color: 'warning' },
  design_fallback: { label: 'design photo', title: 'Sizeless design file (not a photo of this size)', color: 'secondary' },
};

// Download a Response (from apiCall) as a file, honouring Content-Disposition.
export const saveResponseAsFile = async (response, fallbackName) => {
  const blob = await response.blob();
  let name = fallbackName;
  const cd = response.headers && response.headers.get('Content-Disposition');
  if (cd) {
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    if (star) { try { name = decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch (e) { /* keep fallback */ } }
    else if (plain) name = plain[1].trim();
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// ---------------------------------------------------------------------------
// Polling: calls fn every intervalMs while `enabled`; skips while the tab is
// hidden (SPEC 5.2) and fires once immediately when the page becomes visible.
// Never overlaps two calls.
// ---------------------------------------------------------------------------
export const usePolling = (fn, intervalMs, enabled) => {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let inFlight = false;
    let timer = null;
    const schedule = () => { if (!stopped) timer = setTimeout(tick, intervalMs); };
    async function tick() {
      if (stopped) return;
      if (document.hidden) { schedule(); return; }
      inFlight = true;
      try { await fnRef.current(); } catch (e) { /* the caller shows its own errors */ }
      inFlight = false;
      schedule();
    }
    const onVisible = () => {
      if (!document.hidden && !stopped && !inFlight) {
        clearTimeout(timer);
        tick();
      }
    };
    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled]);
};

// Re-render every intervalMs (default 1 s: elapsed timers on queued / generating rows;
// half-price rows only need a coarse tick).
export const useNow = (enabled, intervalMs = 1000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [enabled, intervalMs]);
  return now;
};

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------
export const NyTime = ({ ts, variant = 'body2', color }) => {
  if (!ts) return <Typography variant={variant} color="text.disabled" component="span">-</Typography>;
  return (
    <Tooltip title={`${fmtNyFull(ts)} (New York)`}>
      <Typography variant={variant} color={color} component="span" sx={{ whiteSpace: 'nowrap' }}>{fmtNy(ts)}</Typography>
    </Tooltip>
  );
};

export const CostText = ({ usd, digits = 2, possible, variant = 'body2' }) => (
  <Typography variant={variant} component="span" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
    {fmtUsd(usd, digits)}
    {possible ? (
      <Tooltip title="Outcome unknown (timed out after sending, or a half-price image Google never returned): Google may or may not have billed them.">
        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
          (+{fmtUsd(possible, digits)} possible)
        </Typography>
      </Tooltip>
    ) : null}
  </Typography>
);

export const StyleChip = ({ code, name, styles, onClick, size = 'small', title }) => {
  const label = name || styleName(code, styles) || 'No style';
  const c = styleColor(code);
  const chip = (
    <Chip
      size={size}
      label={label}
      onClick={onClick}
      sx={{
        bgcolor: `${c}1F`, color: c, border: `1px solid ${c}55`, fontWeight: 600,
        cursor: onClick ? 'pointer' : 'default',
      }}
    />
  );
  return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
};

// The second shot of an outdoor + sunroom rug (scene 'sun', build spec 4.4).
export const SUNROOM_SHOT_TIP = 'Second shot of an outdoor + sunroom rug, in a sunroom';
export const SunroomShotChip = ({ size = 'small' }) => {
  const c = STYLE_COLORS.SU;
  return (
    <Tooltip title={SUNROOM_SHOT_TIP}>
      <Chip
        size={size} icon={<MdLightMode size={14} />} label="Sunroom shot"
        sx={{ bgcolor: `${c}24`, color: '#8D6E00', border: `1px solid ${c}88`, fontWeight: 600, '& .MuiChip-icon': { color: '#B58900' } }}
      />
    </Tooltip>
  );
};

export const StatusChip = ({ map, status, title, size = 'small', variant }) => {
  const info = (map && map[status]) || { label: status || '-', color: 'default' };
  const chip = <Chip size={size} label={info.label} color={info.color} variant={variant || (info.color === 'default' ? 'outlined' : 'filled')} />;
  return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
};

export const SiloKindBadge = ({ kind }) => {
  const info = SILO_KIND[kind];
  if (!info) return null;
  return (
    <Tooltip title={info.title}>
      <Chip size="small" label={info.label} color={info.color} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
    </Tooltip>
  );
};

export const FlagIcons = ({ flags }) => {
  const list = Array.isArray(flags) ? flags : String(flags || '').split(',').map((f) => f.trim()).filter(Boolean);
  if (!list.length) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.25, alignItems: 'center', ml: 0.5, color: 'text.secondary' }}>
      {list.map((f) => {
        const info = STYLE_FLAG_INFO[f];
        const Icon = info ? info.icon : MdWarningAmber;
        return (
          <Tooltip key={f} title={info ? info.text : f}>
            <span style={{ display: 'inline-flex' }}><Icon size={14} /></span>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// A square image tile. No lazy loading and no hidden prefetch (CLAUDE.md gotcha):
// it renders exactly the images that are on screen.
export const Tile = ({ src, alt, size = 64, onClick, children, contain = true, sx }) => {
  const theme = useTheme();
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  return (
    <Box
      onClick={onClick}
      sx={{
        width: size, height: size, flex: '0 0 auto', position: 'relative', borderRadius: 1, overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f3f0',
        border: `1px solid ${theme.palette.divider}`,
        cursor: onClick ? 'zoom-in' : 'default',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...sx,
      }}
    >
      {src && !broken ? (
        <Box
          component="img" src={src} alt={alt || ''} onError={() => setBroken(true)}
          sx={{ width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover', display: 'block' }}
        />
      ) : (
        <MdBrokenImage size={Math.max(16, Math.round(size / 4))} style={{ opacity: 0.3 }} />
      )}
      {children}
    </Box>
  );
};

// Lightbox that flips between silo / base / result (SPEC 5.3 Review).
// items: [{ key, label, src, downloadUrl, downloadLabel }]
export const ImageLightbox = ({ open, onClose, items, initialKey, title }) => {
  const [active, setActive] = useState(initialKey);
  useEffect(() => { if (open) setActive(initialKey); }, [open, initialKey]);
  const list = (items || []).filter((x) => x && x.src);
  const cur = list.find((x) => x.key === active) || list[0];

  const onKey = useCallback((e) => {
    if (!list.length) return;
    const idx = list.findIndex((x) => x.key === (cur && cur.key));
    if (e.key === 'ArrowRight') { setActive(list[(idx + 1) % list.length].key); e.preventDefault(); }
    if (e.key === 'ArrowLeft') { setActive(list[(idx - 1 + list.length) % list.length].key); e.preventDefault(); }
  }, [list, cur]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth onKeyDown={onKey}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 6 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }} noWrap>{title}</Typography>
        {list.length > 1 && (
          <ToggleButtonGroup size="small" exclusive value={cur ? cur.key : null} onChange={(e, v) => v && setActive(v)}>
            {list.map((x) => <ToggleButton key={x.key} value={x.key}>{x.label}</ToggleButton>)}
          </ToggleButtonGroup>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close"><MdClose /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'action.hover', minHeight: 300 }}>
        {cur ? (
          <Box component="img" src={cur.src} alt={cur.label} sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
        ) : <Typography color="text.secondary">No image</Typography>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">Left / right arrow keys flip between images.</Typography>
        {cur && cur.downloadUrl && (
          <Button startIcon={<MdDownload />} component={Link} href={cur.downloadUrl} target="_blank" rel="noopener noreferrer">
            {cur.downloadLabel || 'Download original'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmDialog = ({
  open, title, children, confirmLabel = 'Confirm', confirmColor, busy, onConfirm, onClose, confirmDisabled,
}) => (
  <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>Cancel</Button>
      <Button
        variant="contained" onClick={onConfirm} disabled={busy || confirmDisabled}
        color={confirmColor || 'primary'}
        sx={confirmColor ? undefined : { bgcolor: AI_COLOR, '&:hover': { bgcolor: AI_HOVER } }}
      >
        {busy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export const SectionTitle = ({ children, right }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{children}</Typography>
    {right}
  </Stack>
);

// White label on merlot in both themes: the dark theme's primary contrast text is near-black (2.1:1 on #722F37).
export const primaryButtonSx = { bgcolor: AI_COLOR, color: '#fff', '&:hover': { bgcolor: AI_HOVER } };

// Event payloads may arrive as objects or as JSON strings (MySQL JSON column).
export const parseJsonish = (v) => {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (e) { return v; }
};

// Message for a failed apiCall, with the 403 access gate worded for people.
// A 409 daily_limit carries the server's friendly text (remaining $ and the limit).
export const FORBIDDEN_TEXT = 'You do not have access to Rug Room Shots. Access is by role (administrators and the Photo Department); ask an administrator.';
export const errText = (e) => {
  if (!e) return '';
  if (e.status === 403) return FORBIDDEN_TEXT;
  if (isDailyLimit(e)) return dailyLimitText(e);
  return e.message || 'Request failed';
};

// An earlier attempt of a rug (Review with "Include earlier attempts", build spec 4.2): view-only; its checkbox
// ticks the rug's current image (targetLatestGenId).
export const isEarlierAttempt = (it) => !!it && (it.targetLatestGenId
  ? Number(it.targetLatestGenId) !== Number(it.genId)
  : it.isLatest === false);

// Sunroom shots never take "Wrong style" (their style is always Sunroom); the reason stays listed (disabled) so the
// compare view's number keys keep their meaning.
export const reasonBlocked = (item, reason) => reason === 'wrong_style' && !!item && item.scene === 'sun';
export const WRONG_STYLE_TWIN_TIP = 'Sunroom shots always use Sunroom rooms';

// 409 { error, code, current } refusals: the code, and the server's own words (shown as a warning toast).
export const conflictCode = (e) => (e && e.status === 409 && e.body && e.body.code ? e.body.code : null);
export const serverText = (e) => (e && e.status !== 403 && e.body && e.body.error ? e.body.error : errText(e));
// Refusals that only need the server's message, as a warning (build spec 9.3 / 9.5).
export const WARN_CODES = [
  'no_detail_photo', 'detail_not_found', 'worker_not_ready', 'twin_style_fixed', 'scene_list_only', 'scene_room_style',
  'room_not_active', 'no_base', 'room_not_found', 'room_shape_mismatch', 'not_replannable', 'not_cancellable', 'nothing_to_export',
  'replanning',
  // AI check (SPEC_AICHECK 5.2)
  'checking', 'outputs_not_ready', 'not_succeeded', 'nothing_eligible',
];

// ---------------------------------------------------------------------------
// Daily spend limit for non-admins ($1,000 per person per New York day; admins unlimited).
// GET /config returns isAdmin, dailyLimitUsd (null = unlimited), spentTodayUsd, remainingUsd.
// Every paid request (Start, Generate remaining, Regenerate / Retry, bulk regenerate) can be
// refused with 409 { code: 'daily_limit', error, limitUsd, spentTodayUsd, remainingUsd }.
// Nothing is queued when that happens and nothing retries it automatically.
// ---------------------------------------------------------------------------
export const isDailyLimit = (e) => !!(e && e.status === 409 && e.body && e.body.code === 'daily_limit');

export const dailyLimitText = (e) => {
  const b = (e && e.body) || {};
  if (b.error) return b.error;
  const parts = ['Daily spending limit reached; nothing was queued.'];
  if (b.remainingUsd !== undefined && b.remainingUsd !== null) parts.push(`${fmtUsd(b.remainingUsd)} left today`);
  if (b.limitUsd !== undefined && b.limitUsd !== null) parts.push(`of your ${fmtUsd(b.limitUsd, 0)} daily limit (New York day).`);
  return parts.join(' ');
};

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// The caller's budget from /config (top-level fields, or a nested "budget" object).
// limitUsd null = unlimited (admins / owners, or the limit switched off).
export const budgetFromConfig = (config) => {
  const c = config || {};
  const b = c.budget && typeof c.budget === 'object' ? { ...c, ...c.budget } : c;
  const limitUsd = numOrNull(b.dailyLimitUsd);
  const spentTodayUsd = numOrNull(b.spentTodayUsd);
  let remainingUsd = numOrNull(b.remainingUsd);
  if (remainingUsd === null && limitUsd !== null && spentTodayUsd !== null) remainingUsd = Math.max(0, limitUsd - spentTodayUsd);
  return {
    isAdmin: typeof b.isAdmin === 'boolean' ? b.isAdmin : null,
    limited: limitUsd !== null && limitUsd > 0,
    limitUsd,
    spentTodayUsd,
    remainingUsd,
  };
};

// True when a non-admin's estimate is above what is left today (a hint; the server decides).
export const overBudget = (config, usd) => {
  const bud = budgetFromConfig(config);
  if (!bud.limited || bud.remainingUsd === null) return false;
  return Number(usd || 0) > bud.remainingUsd + 0.00001;
};

// Words for the hint under a paid button when the estimate is above what is left today.
export const overBudgetText = (config, usd) => {
  const bud = budgetFromConfig(config);
  return `This is about ${fmtUsd(usd)}, but you have ${fmtUsd(bud.remainingUsd)} left of your ${fmtUsd(bud.limitUsd, 0)} daily limit (New York day). The server will refuse it; choose fewer images or half price, or wait until tomorrow.`;
};

// After anything that may have spent money (or was refused for the limit), the page
// re-reads /config so the "Today: $X of $1,000 used" indicator stays current.
// A window event keeps the deep dialogs from threading another prop through every tab.
export const RRS_SPEND_EVENT = 'rrs-spend-changed';
export const signalSpend = () => {
  try { window.dispatchEvent(new Event(RRS_SPEND_EVENT)); } catch (e) { /* old browsers: the minute poll catches up */ }
};

// ---------------------------------------------------------------------------
// AI check (SPEC_AICHECK 2.1 / 2.5 / 5.2). The backend sends an `aiCheck` block per finished image:
// { state: pass | look | fail | unchecked | checking | error, checking, checkId, score, scoreModel, hardFail,
//   hardFailCodes, passBlocked, why, problems, suggestedNote, criteria [{ id, result, note }], checkedAt, origin,
//   model, promptVersion, costUsd, lastError: { checkId, errorCode, errorText, at } | null }
// and null for an image that is not finished. An older backend sends no block at all (undefined): the page then
// hides every AI control. The AI never approves, rejects or regenerates by itself.
// ---------------------------------------------------------------------------
export const AI_NOT_READY_TEXT = 'AI check is not running yet (the worker has not been updated).';

export const AI_STATE = {
  pass: { label: 'Pass', color: 'success' },
  look: { label: 'Needs a look', color: 'warning' },
  fail: { label: 'Fail', color: 'error' },
  unchecked: { label: 'Not checked', color: 'default' },
  checking: { label: 'Checking', color: 'info' },
  error: { label: 'Could not check', color: 'default' },
};

// The Review "AI check" filter: [value, label]; '' = no AI filter.
export const AI_FILTERS = [
  ['', 'All'], ['fail', 'Fail'], ['look', 'Needs a look'], ['not_pass', 'Fail or needs a look'], ['pass', 'Pass'],
  ['unchecked', 'Not checked'], ['error', 'Could not check'], ['checking', 'Checking'],
];

export const AI_CRITERIA = ['design', 'colors', 'edges', 'texture', 'shape', 'scale', 'placement', 'room', 'defects'];
export const AI_CRITERIA_LABELS = {
  design: 'Design and pattern',
  colors: 'Colors',
  edges: 'Edge, binding and fringe',
  texture: 'Pile and texture',
  shape: 'Shape and proportions',
  scale: 'Size in the room',
  placement: 'Placement',
  room: 'Room unchanged',
  defects: 'Image defects',
};
export const AI_RESULT_LABELS = {
  ok: 'OK', minor: 'Small issue', major: 'Problem', not_sure: 'Not sure',
};
export const AI_HARD_FAIL_LABELS = {
  rug_missing: 'Rug missing',
  design_different: 'Different design',
  wrong_shape: 'Wrong shape',
  room_changed: 'Room changed',
  extra_rug: 'Extra rug',
  rug_cut_off: 'Rug cut off',
};

const AI_RESULT_STATES = ['pass', 'look', 'fail'];
// True when the image has an AI result (a done check), also while it is being checked again.
export const aiHasResult = (ai) => !!ai && AI_RESULT_STATES.includes(ai.state);

// The criteria of a result in the fixed order (unknown ids after them), tolerant of a JSON string.
export const aiCriteriaList = (ai) => {
  const raw = ai ? parseJsonish(ai.criteria) : null;
  const list = Array.isArray(raw) ? raw.filter((c) => c && c.id) : [];
  const rank = (id) => { const i = AI_CRITERIA.indexOf(id); return i < 0 ? 99 : i; };
  return [...list].sort((a, b) => rank(a.id) - rank(b.id));
};

// "Rug binding, Room changed" (the problems as reject-reason labels).
export const aiProblemsText = (ai) => {
  const list = ai && Array.isArray(ai.problems) ? ai.problems : [];
  return list.map((p) => REJECT_REASON_LABELS[p] || String(p).replace(/_/g, ' ')).join(', ');
};

// One line for an image's AI state (the compare view's strip, tooltips).
export const aiWhyLine = (ai) => {
  if (!ai) return '';
  if (aiHasResult(ai)) {
    const probs = ai.state === 'pass' ? '' : aiProblemsText(ai);
    return `${ai.why || AI_STATE[ai.state].label}${probs ? ` (${probs})` : ''}`;
  }
  if (ai.state === 'checking') return 'Checking with AI…';
  if (ai.state === 'error') return `Could not check: ${(ai.lastError && ai.lastError.errorText) || 'the AI check failed.'}`;
  return 'Not checked by AI yet.';
};

// The reject reason the AI suggests for an image it failed or flagged: its first problem, when that is one of the
// reasons on offer and allowed for this image, and the image has no reason yet. The person can change it.
export const aiSuggestedReason = (item, rejectReasons) => {
  const ai = item && item.aiCheck;
  if (!ai || item.reviewReasonCode || (ai.state !== 'fail' && ai.state !== 'look')) return null;
  const first = Array.isArray(ai.problems) && ai.problems.length ? ai.problems[0] : null;
  if (!first || !(rejectReasons || []).includes(first) || reasonBlocked(item, first)) return null;
  return first;
};

// "120 pass · 30 need a look · 12 fail · 5 not checked" (zero parts left out).
export const aiCountsText = (c) => {
  if (!c) return '';
  const parts = [];
  const add = (n, one, many) => {
    const v = Number(n) || 0;
    if (v > 0) parts.push(`${fmtInt(v)} ${v === 1 ? one : many}`);
  };
  add(c.pass, 'pass', 'pass');
  add(c.look, 'needs a look', 'need a look');
  add(c.fail, 'fail', 'fail');
  add(c.unchecked, 'not checked', 'not checked');
  add(c.error, 'could not be checked', 'could not be checked');
  add(c.checking, 'checking', 'checking');
  return parts.join(' · ');
};

// The AI chip of an image: "AI 92 · Pass" (filled, the verdict's colour), "AI · Checking" (spinner),
// "AI · Could not check" (warning icon) or "AI · Not checked" (outlined grey). A result that is being checked again
// keeps its colour and gets the small spinner. The tooltip carries the reason, when and with which model / prompt.
export const AiChip = ({ ai, size = 'small', sx }) => {
  if (!ai) return null;
  const has = aiHasResult(ai);
  const info = AI_STATE[ai.state] || AI_STATE.unchecked;
  const spinner = <CircularProgress size={12} color="inherit" />;
  let label;
  let chipProps;
  if (has) {
    const score = ai.score !== null && ai.score !== undefined ? ` ${ai.score}` : '';
    label = `AI${score} · ${info.label}`;
    chipProps = { color: info.color, variant: 'filled', icon: ai.checking ? spinner : undefined };
  } else if (ai.state === 'checking') {
    label = 'AI · Checking';
    chipProps = { color: 'info', variant: 'outlined', icon: spinner };
  } else if (ai.state === 'error') {
    label = 'AI · Could not check';
    chipProps = { color: 'default', variant: 'outlined', icon: <MdWarningAmber size={14} /> };
  } else {
    label = 'AI · Not checked';
    chipProps = { color: 'default', variant: 'outlined' };
  }

  let title;
  if (has) {
    const meta = [ai.checkedAt ? `Checked ${fmtNy(ai.checkedAt)}` : null, ai.model, ai.promptVersion].filter(Boolean).join(' · ');
    title = (
      <Box sx={{ maxWidth: 360 }}>
        {ai.why ? <Box>{ai.why}</Box> : null}
        {ai.state !== 'pass' && aiProblemsText(ai) ? <Box sx={{ mt: 0.5 }}>Problems: {aiProblemsText(ai)}</Box> : null}
        {meta ? <Box sx={{ mt: 0.5, opacity: 0.85 }}>{meta}</Box> : null}
        {ai.checking ? <Box sx={{ mt: 0.5 }}>Checking again…</Box> : null}
        {ai.lastError ? <Box sx={{ mt: 0.5 }}>A newer check could not finish: {ai.lastError.errorText || 'the AI check failed.'}</Box> : null}
      </Box>
    );
  } else if (ai.state === 'checking') {
    title = 'Checking with AI…';
  } else if (ai.state === 'error') {
    title = `Could not check: ${(ai.lastError && ai.lastError.errorText) || 'the AI check failed.'}`;
  } else {
    title = 'Not checked by AI yet.';
  }

  return (
    <Tooltip title={title}>
      <Chip size={size} label={label} {...chipProps} sx={{ fontWeight: has ? 700 : 500, ...sx }} />
    </Tooltip>
  );
};
